import { AccButton } from "@components/base/AccButton";
import React, { useEffect, useState } from "react";
import { ExclamationIcon, CheckIcon } from "@heroicons/react/outline";
import Portal from "@utils/portal";
import { useModal } from "./ModalContext";
import { DeepNonNullable } from "ts-essentials";
import { graphql } from "gatsby";

export const query = graphql`
  query ErrorModal($locale: String!) {
    strapiGenericText(locale: { eq: $locale }) {
      errorText
      closeText
    }
  }
`;

export enum InfoType {
  Error,
  Success,
}

const ErrorModal: React.FC<{
  data: DeepNonNullable<Queries.ErrorModalQuery>;
  sliceContext: {
    locale: string;
  };
}> = ({ data }) => {
  const { errorText, closeText } = data.strapiGenericText;
  const {
    isErrorModalOpen,
    errorModalMessage = "",
    hideErrorModal,
    showErrorModal,
  } = useModal();
  const [infoType, setInfoType] = useState<InfoType>(InfoType.Error);

  useEffect(() => {
    const error = new URLSearchParams(window.location.search).get("error");
    if (error) {
      showErrorModal(error);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleClose = () => {
    hideErrorModal();
  };

  if (!isErrorModalOpen) return null;
  return (
    <Portal>
      <div
        className="fixed inset-0 z-40 overflow-y-auto"
        aria-labelledby="modal-title"
        role="dialog"
        aria-modal="true"
      >
        <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            aria-hidden="true"
          />

          <span
            className="hidden sm:inline-block sm:h-screen sm:align-middle"
            aria-hidden="true"
          >
            &#8203;
          </span>

          <div className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div
                  className={`mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10 ${
                    infoType == InfoType.Error ? "bg-red-100" : "bg-green-100"
                  }`}
                >
                  {infoType == InfoType.Error && (
                    <ExclamationIcon className="h-6 w-6 text-red-600" />
                  )}
                  {infoType == InfoType.Success && (
                    <CheckIcon className="h-6 w-6 text-green-600" />
                  )}
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <p className="text-2xl">{errorText}</p>
                  <p>{errorModalMessage}</p>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <AccButton
                onClick={handleClose}
                type="button"
                className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                {closeText}
              </AccButton>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
};

export default ErrorModal;
